import {
  CheckCircle,
  CubeTransparent,
  PauseCircle,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import { Loading } from '@whistler-ui/k2/components/Loading';
import { Tooltip } from '@whistler-ui/k2/components/Tooltip';
import { toSnakeCase } from '@whistler-ui/k2/helpers';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CANCELLED,
  CREATED,
  FAILED,
  RESUME,
  RUNNING,
  SUCCESS,
  TERMINATED,
} from '../../../constants';
import { useThemeStore } from '../../../store/theme';
import { TStatuses, TUseCaseType } from '../../../types';

type TestIconProps = {
  status: TStatuses;
  isFetching: boolean;
  testTag: string;
  type: TUseCaseType;
};

const ICON_SIZE = 24;
const componentName = 'c-TestTable';

export const TestIcon = ({
  status,
  isFetching,
  testTag,
  type,
}: TestIconProps) => {
  const { t } = useTranslation();
  const { colors } = useThemeStore();
  const c = useClassHelper(componentName);

  const { content, contentClass } = useMemo(() => {
    switch (status) {
      case CREATED:
      case RESUME:
      case RUNNING: {
        return {
          contentClass: 'loading',
          content: <Loading size={ICON_SIZE} aria-label="loading task icon" />,
        };
      }
      case SUCCESS: {
        return {
          contentClass: 'icons',
          content: (
            <CheckCircle
              size={ICON_SIZE}
              color={colors.success}
              weight="fill"
            />
          ),
        };
      }
      case CANCELLED:
      case FAILED: {
        return {
          contentClass: 'icons',
          content: (
            <WarningCircle
              size={ICON_SIZE}
              color={colors.error}
              weight="fill"
              aria-label="warning task icon"
            />
          ),
        };
      }
      case TERMINATED: {
        return {
          contentClass: 'icons',
          content: (
            <XCircle
              size={ICON_SIZE}
              color={colors.error}
              weight="fill"
              aria-label="terminated task icon"
            />
          ),
        };
      }
      default: {
        return {
          contentClass: 'icons',
          content: (
            <PauseCircle
              size={ICON_SIZE}
              color={colors.test}
              weight="fill"
              aria-label="paused task icon"
            />
          ),
        };
      }
    }
  }, [status]);

  if (!status) {
    return (
      <div className={c('__result')}>
        <div>
          <CubeTransparent size={ICON_SIZE} fillOpacity={0} />
        </div>
      </div>
    );
  }

  if (isFetching) {
    return (
      <div className={c('__result')}>
        <div className={c(`__loading`)}>
          <Loading size={ICON_SIZE} />
        </div>
      </div>
    );
  }

  return (
    <div className={c('__result')}>
      <div
        className={c(`__${contentClass}`)}
        data-test-id={`${testTag}`}
        data-test-status={`${toSnakeCase(type)}-${status.toLowerCase()}`}
        data-cy={`test-icon-${status.toLowerCase()}`}
      >
        <Tooltip
          align="left"
          trigger={content}
          contentClassName="max-w-[300px]"
          content={t(status)}
        />
      </div>
    </div>
  );
};
